import {
  TIMEZONES,
  type DatabaseRecord,
  type DatabaseRecordValue,
  type FormField,
  type FormResponseValue,
  type FormResponseValueAnswer,
  type Timezone,
} from "@tellescope/types-models";
import { deriveComputedValueKey } from "./derive-computed-value-key";

export interface ResolveDatabasePrepopulationArgs {
  fieldsArr: FormField[];
  records: DatabaseRecord[];
  currentResponses: Record<string, FormResponseValue>;
}

const columnValues = (record: DatabaseRecord, label: string): DatabaseRecordValue[] =>
  record.values.filter((value) => value.label === label);

/**
 * An `Address` answer is an object that starts out with no keys, so emptiness is decided by whether
 * any part was filled in — legacy made the same distinction (`hooks.tsx:867-871`), and without it a
 * blank address counts as an existing value and blocks prepopulation.
 */
const isValueEmpty = (value: unknown) => {
  if (value === undefined || value === null || value === "") return true;
  if (typeof value === "object" && !Array.isArray(value)) return Object.keys(value).length === 0;
  return false;
};

/**
 * A database `Address` names its street lines `lineOne`/`lineTwo`, while a form's `Address` answer
 * names them `addressLineOne`/`addressLineTwo`. Legacy renamed them on the way across
 * (`hooks.tsx:905-919`); without it the street lines are dropped from every prepopulated address.
 */
const asAddressAnswer = (column: DatabaseRecordValue) => {
  if (column.type !== "Address" || !column.value) return undefined;
  const { lineOne, lineTwo, ...rest } = column.value;
  return {
    ...rest,
    addressLineOne: lineOne || "",
    ...(lineTwo ? { addressLineTwo: lineTwo } : {}),
  };
};

/**
 * Renders one column as text for a `string`/`stringLong` field, matching the subset of column types
 * legacy accepted (`hooks.tsx:921-936`). A list joins with ", "; anything else contributes nothing.
 */
const asTextValue = (column: DatabaseRecordValue): string | undefined => {
  if (column.type === "Text" || column.type === "Text Long") return column.value || undefined;
  if (column.type === "Number") return typeof column.value === "number" ? String(column.value) : undefined;
  if (column.type === "Multiple Select" || column.type === "Text List") {
    return column.value?.length ? column.value.join(", ") : undefined;
  }
  return undefined;
};

const coerceForFieldType = (
  type: FormField["type"],
  columns: DatabaseRecordValue[]
): FormResponseValueAnswer["value"] | undefined => {
  switch (type) {
    case "Timezone":
      // Only a recognized zone may be written, since the field renders a fixed list and an unknown
      // string would leave it displaying nothing.
      return columns.reduce<Timezone | undefined>(
        (found, column) =>
          found ?? TIMEZONES.find((timezone) => typeof column.value === "string" && timezone === column.value),
        undefined
      );
    case "Address":
      return columns.reduce<ReturnType<typeof asAddressAnswer>>(
        (found, column) => found ?? asAddressAnswer(column),
        undefined
      );
    case "string":
    case "stringLong": {
      // Legacy accumulated every matching column and joined them, so a field mapped to a column that
      // repeats across records receives all of the values rather than only the first.
      const texts = columns.reduce<string[]>((acc, column) => {
        const text = asTextValue(column);
        return text ? [...acc, text] : acc;
      }, []);
      return texts.length ? texts.join(" \n\n") : undefined;
    }
    default:
      return undefined;
  }
};

export const resolveDatabasePrepopulation = ({
  fieldsArr,
  records,
  currentResponses,
}: ResolveDatabasePrepopulationArgs): Record<string, FormResponseValue> =>
  fieldsArr.reduce<Record<string, FormResponseValue>>((acc, field) => {
    const config = field.prepopulateFromDatabase;
    if (!config?.field || !config.databaseId) return acc;

    const existing = currentResponses[field.id];
    if (!config.overwrite && !isValueEmpty(existing?.answer.value)) return acc;

    const columns = records.reduce<DatabaseRecordValue[]>(
      (acc, record) =>
        record.databaseId === config.databaseId
          ? [...acc, ...columnValues(record, config.field ?? "").filter((column) => !isValueEmpty(column.value))]
          : acc,
      []
    );
    if (columns.length === 0) return acc;

    const coerced = coerceForFieldType(field.type, columns);
    if (coerced === undefined) return acc;

    // @ts-expect-error Union type gets in the way here
    const answer: FormResponseValueAnswer = { type: field.type, value: coerced };
    acc[field.id] = {
      ...existing,
      fieldId: field.id,
      fieldTitle: field.title,
      answer,
      computedValueKey: deriveComputedValueKey(field.intakeField, answer),
    };
    return acc;
  }, {});
